import React, { useState, useEffect } from 'react';
import { 
  Container, 
  TextField, 
  Button, 
  Typography, 
  Box,
  Paper,
  Grid,
  Dialog,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { 
  Autocomplete, 
} from '@mui/material';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';

// Icons
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CategoryIcon from '@mui/icons-material/Category';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import Header from '../Header';
import { local_signage, local_url } from '../Const';

// Custom Theme with Enhanced Styling
const theme = createTheme({
  palette: {
    primary: {
      main: '#2E7D32',
      light: '#66BB6A',
      dark: '#1B5E20',
    },
    background: {
      default: '#F5F5F5',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#333333',
      secondary: '#666666'
    },
    action: {
      hover: 'rgba(46, 125, 50, 0.08)',
    }
  },
  typography: {
    fontFamily: 'Roboto, Arial, sans-serif',
  },
  components: {
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
    },
  },
});

// API function for uploading image to server
const uploadItemImage = async (itemId, std, uniqueNo, imageFile, shortItemName) => {
  try {
    const token = localStorage.getItem('AuthorizationToken');
    
    if (!token) {
      throw new Error('Authentication token not found');
    }

    const formData = new FormData();
    // Pass image in the body
    formData.append('file', imageFile, 'image.jpg');

    // Pass other values as URL parameters
    const url = `${local_url}/api/ItemImage?ItemID=${encodeURIComponent(itemId)}&UniqueNo=${encodeURIComponent(uniqueNo)}&STD=${std}&ShortItemName=${encodeURIComponent(shortItemName || 'Chicken Boneless 1 Kg')}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (!response.ok) {
      throw new Error(`Image upload failed with status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};

// API function to fetch all mapped items
const fetchAllMappedItems = async () => {
  try {
    const token = localStorage.getItem('AuthorizationToken');
    
    if (!token) {
      throw new Error('Authentication token not found');
    }

    const response = await fetch(`${local_url}/api/ItemUnique/GetAll/EAN`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch mapped items: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching mapped items:', error);
    throw error;
  }
};

// API function to check if an item is already mapped
const checkItemMapping = async (itemId, std = 'EAN') => {
  try {
    const token = localStorage.getItem('AuthorizationToken');
    
    if (!token) {
      throw new Error('Authentication token not found');
    }

    const response = await fetch(`${local_url}/api/ItemUnique/GetById/${itemId}/${std}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.status === 404) {
      // Item not found, which means it's not mapped
      return { isAlreadyMapped: false };
    }

    if (!response.ok) {
      throw new Error(`Failed to check item mapping: ${response.status}`);
    }

    const data = await response.json();
    return {
      isAlreadyMapped: true,
      mappingData: data
    };
  } catch (error) {
    console.error('Error checking item mapping:', error);
    throw error;
  }
};

// Fetch item image
const fetchItemImage = async (itemId) => {
  try {
    const token = localStorage.getItem('AuthorizationToken');
    
    if (!token) {
      throw new Error('Authentication token not found');
    }

    const response = await fetch(`${local_url}/api/ItemImage/${itemId}/EAN`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch item image: ${response.status}`);
    }

    // Check content type to determine if response is image
    const contentType = response.headers.get('content-type');
    
    if (contentType && contentType.includes('image')) {
      const imageBlob = await response.blob();
      return { imageBlob };
    } else {
      return { imageBlob: null };
    }
  } catch (error) {
    console.error('Error fetching item image:', error);
    throw error;
  }
};

export const EAN = ()=> {
  // State management
  const [selectedItem, setSelectedItem] = useState(null);
  const [ean, setEan] = useState('');
  const [std, setStd] = useState('EAN'); // Default value, can be changed
  const [uniqueNo, setUniqueNo] = useState('');
  const [image, setImage] = useState(null);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [mappedData, setMappedData] = useState([]);
  const [itemList, setItemList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [fetchingItemData, setFetchingItemData] = useState(false);
  const [loadingThumbnail, setLoadingThumbnail] = useState(false);
  const [isItemAlreadyMapped, setIsItemAlreadyMapped] = useState(false);
  const [fetchingMappedItems, setFetchingMappedItems] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [shortItemName, setShortItemName] = useState('');


  // Fetch items from API
  const fetchItems = async (query = 'Chicken curry') => {
    setLoading(true);
    try {
      const token = localStorage.getItem('AuthorizationToken');
      const response = await fetch(`${local_signage}/api/v1/itemsearchlist?partNumber=${query}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }
      
      const data = await response.json();
      // Format the data for the dropdown
      const formattedData = data.map(item => ({
        code: item.ItemID,
        name: item.ItemName,
        // Add a chicken emoji as icon
        icon: "🍗"
      }));
      
      setItemList(formattedData);
    } catch (error) {
      console.error('Error fetching data:', error);
      // Set fallback data in case API fails
      setItemList([
        { code: "1000000016", name: "Chicken Curry Cut Ws 1Kg", icon: "🍗" },
        { code: "1000000017", name: "Chicken Curry Cut Wos 1Kg", icon: "🍗" },
      ]);
      showSnackbar('Error fetching chicken products. Using fallback data.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all mapped items and update the table
  const loadMappedItems = async () => {
    setFetchingMappedItems(true);
    try {
      const items = await fetchAllMappedItems();
      
      // Format the data for the table
      const formattedData = items.map((item, index) => ({
        sno: index + 1,
        itemCode: item.itemID,
        itemName: item.itemName,
        eanNumber: item.uniqueNo,
        std: item.std,
        uniqueNo: item.uniqueNo,
        thumbnail: item.unImage ? `data:${item.contentType};base64,${item.unImage}` : null
      }));
      
      setMappedData(formattedData);
    } catch (error) {
      console.error('Error loading mapped items:', error);
      showSnackbar('Failed to load mapped items', 'error');
    } finally {
      setFetchingMappedItems(false);
    }
  };


  const handleShortItemNameChange = (event) => {
    const value = event.target.value;
    // Limit input to 20 characters
    if (value.length <= 20) {
      setShortItemName(value);
    }
  };

  // Fetch items and mapped data on component mount
  useEffect(() => {
    fetchItems();
    loadMappedItems();
  }, []);

  // Set uniqueNo equal to EAN by default when EAN changes
  useEffect(() => {
    setUniqueNo(ean);
  }, [ean]);

  // Debounced search handler
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm) {
        fetchItems(searchTerm);
      }
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Helper function to show snackbar messages
  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const closeSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!selectedItem) {
      errors.selectedItem = 'Please select a chicken product';
    }
    
    if (!ean) {
      errors.ean = 'EAN number is required';
    } else if (ean.length !== 13) {
      errors.ean = 'EAN number must be exactly 13 digits';
    } else if (!/^\d+$/.test(ean)) {
      errors.ean = 'EAN number must contain only digits';
    }
    
    if (!std) {
      errors.std = 'STD value is required';
    }
    if (!shortItemName) {
      errors.shortItemName = 'Short item name is required';
    } else if (shortItemName.length > 20) {
      errors.shortItemName = 'Short item name must be 20 characters or less';
    }
    
    if (!uniqueNo) {
      errors.uniqueNo = 'Unique number is required';
    }
    
    if (!image) {
      errors.image = 'Please upload a product image';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  };

  // Handlers
  const handleEanChange = (event) => {
    const value = event.target.value;
    if (/^\d{0,13}$/.test(value)) {
      setEan(value);
    }
  };

  const handleStdChange = (event) => {
    setStd(event.target.value);
  };

  const handleUniqueNoChange = (event) => {
    setUniqueNo(event.target.value);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showSnackbar('Please upload an image file', 'error');
        return;
      }
      
      setImage({
        preview: URL.createObjectURL(file),
        raw: file
      });
    }
  };

  // Function to load item image
  const loadItemImage = async (itemId) => {
    setLoadingThumbnail(true);
    try {
      const { imageBlob } = await fetchItemImage(itemId);
      
      if (imageBlob) {
        setImage({
          preview: URL.createObjectURL(imageBlob),
          raw: imageBlob
        });
      } else {
        setImage(null);
      }
    } catch (error) {
      console.error('Error loading item image:', error);
      setImage(null);
    } finally {
      setLoadingThumbnail(false);
    }
  };

  // Handler for checking if an item is already mapped
  const checkIfItemIsMapped = async (itemId) => {
    try {
      setFetchingItemData(true);
      const result = await checkItemMapping(itemId);
      
      setIsItemAlreadyMapped(result.isAlreadyMapped);
      
      if (result.isAlreadyMapped) {
        const { mappingData } = result;
        setEan(mappingData.uniqueNo || '');
        setUniqueNo(mappingData.uniqueNo || '');
        showSnackbar(`Item ${itemId} is already mapped`, 'info');
        
        // Load the item image
        await loadItemImage(itemId);
      } else {
        // Reset the image if the item is not mapped
        setImage(null);
        // Display "No EAN Mapped" in the field
        setEan('');
        setUniqueNo('');
        showSnackbar(`No EAN mapping found for item ${itemId}`, 'info');
      }
      
      return result.isAlreadyMapped;
    } catch (error) {
      console.error('Error checking if item is mapped:', error);
      showSnackbar('No EAN mapped with this Item', 'error');
      setIsItemAlreadyMapped(false);
      return false;
    } finally {
      setFetchingItemData(false);
    }
  };

  // Handler for auto-fetching EAN and image when an item is selected
  const handleItemSelectionChange = async (event, newValue) => {
    setSelectedItem(newValue);
    
    if (newValue) {
      // Reset state for new selection
      setEan('');
      setUniqueNo('');
      setImage(null);
      
      // Check if item is already mapped
      await checkIfItemIsMapped(newValue.code);
      
      // Note: We've removed the dummy EAN code logic
      // The EAN field will remain empty if not mapped
    } else {
      // Reset state when no item is selected
      setEan('');
      setUniqueNo('');
      setImage(null);
      setIsItemAlreadyMapped(false);
    }
  };

  // Handler for manual refresh of mapped items
  const handleRefreshMappedItems = () => {
    loadMappedItems();
  };

  const handleSubmit = async () => {
    const { isValid, errors } = validateForm();
    
    if (!isValid) {
      // Display errors to the user
      const errorMessage = Object.values(errors).join('\n');
      showSnackbar(errorMessage, 'error');
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Upload the image to the server
      await uploadItemImage(selectedItem.code, std, uniqueNo, image.raw, shortItemName);
      
      // Refresh the mapped items table
      await loadMappedItems();
      
      // Reset form
      setSelectedItem(null);
      setEan('');
      // Keep STD value for next item
      setUniqueNo('');
      setImage(null);
      setShortItemName('');
      setIsItemAlreadyMapped(false);
      
      // Show success message
      showSnackbar('Product mapped successfully with image uploaded', 'success');
    } catch (error) {
      showSnackbar(`Error: ${error.message}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteMappedItem = (sno) => {
    // In a real application, you would call an API to delete the mapping
    // For now, we'll just remove it from the local state
    setMappedData(mappedData.filter(item => item.sno !== sno));
    showSnackbar('Mapping deleted', 'info');
  };

  const handleSearchInputChange = (event, value) => {
    setSearchTerm(value);
  };

  return (
    <>
    <Header/>
    <ThemeProvider theme={theme}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography 
            variant="h6" 
            align="center" 
            color="primary"
            gutterBottom
            sx={{ 
              mb: 3, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: 2 
            }}
          >
            <CategoryIcon fontSize="large" />
            Product Mapping
          </Typography>

          <Grid container spacing={3}>
            {/* Left side - Form fields */}
            <Grid item xs={12} md={9}>
              {/* First row */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={7.5 }>
                  <Autocomplete
                    fullWidth
                    options={itemList}
                    getOptionLabel={(option) => `${option.code} - ${option.name}`}
                    value={selectedItem}
                    onChange={handleItemSelectionChange}
                    onInputChange={handleSearchInputChange}
                    loading={loading}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Item " 
                        placeholder="Search for a chicken product"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <CategoryIcon 
                                color="action" 
                                fontSize="small"
                                sx={{ mr: 1, color: '#2E7D32' }} 
                              />
                              {params.InputProps.startAdornment}
                            </>
                          ),
                          endAdornment: (
                            <>
                              {loading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <span style={{ marginRight: 10 }}>{option.icon}</span>
                        {option.code} - {option.name}
                      </li>
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="EAN Number"
                    value={ean}
                    onChange={handleEanChange}
                    disabled={isItemAlreadyMapped || fetchingItemData}
                    inputProps={{ maxLength: 13 }}
                    InputProps={{
                      startAdornment: (
                        <ConfirmationNumberIcon 
                          color="action" 
                          fontSize="small"
                          sx={{ mr: 1, color: '#2E7D32' }} 
                        />
                      ),
                      endAdornment: fetchingItemData ? <CircularProgress color="inherit" size={20} /> : null,
                      placeholder: selectedItem && !isItemAlreadyMapped && !fetchingItemData ? "No EAN Mapped" : "",
                    }}
                    placeholder={selectedItem && !isItemAlreadyMapped && !fetchingItemData ? "No EAN Mapped" : ""}
                    helperText={selectedItem && !isItemAlreadyMapped && !fetchingItemData && !ean ? "No EAN Mapped" : ""}
                  />
                </Grid>
                <Grid item xs={12} sm={1.5}>
                  <TextField
                    fullWidth
                    label="STD"
                    value={std}
                    onChange={handleStdChange}
                    disabled={isItemAlreadyMapped || fetchingItemData}
                  />
                </Grid>
              </Grid>
              
              {/* Second row */}
              <Grid container spacing={2}>

              <Grid item xs={12} sm={4}>
              <TextField
  fullWidth
  label="Short Item Name"
  value={shortItemName}
  onChange={handleShortItemNameChange}
  disabled={isItemAlreadyMapped || fetchingItemData}
  inputProps={{ maxLength: 20 }}
  helperText={`${shortItemName.length}/20 characters`}
/>
                </Grid>
              
                <Grid item xs={12} sm={4}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                    id="file-upload"
                    disabled={isItemAlreadyMapped || fetchingItemData}
                  />
                  <label htmlFor="file-upload" style={{ width: '100%' }}>
                    <Button 
                      variant="outlined" 
                      component="span"
                      startIcon={<AddPhotoAlternateIcon />}
                      color="primary"
                      fullWidth
                      disabled={isItemAlreadyMapped || fetchingItemData}
                    >
                      {image ? 'Change Image' : 'Upload Image'}
                    </Button>
                  </label>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={submitting || isItemAlreadyMapped || fetchingItemData}
                    endIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                    fullWidth
                  >
                    {submitting ? 'Submitting...' : 'Submit'}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            
            {/* Right side - Thumbnail */}
            <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Box 
                sx={{ 
                  border: '1px dashed #2E7D32', 
                  borderRadius: 2, 
                  p: 1, 
                  display: 'flex', 
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  height: '50%',
                  minHeight: '80px'
                }}
              >
                {loadingThumbnail ? (
                  <CircularProgress size={40} color="primary" />
                ) : image ? (
                  <Tooltip title="Click to view larger image">
                    <img 
                      src={image.preview} 
                      alt="Product Preview" 
                      style={{
                        maxWidth: '100%',
                        maxHeight: '80px',
                        objectFit: 'contain',
                        cursor: 'pointer'
                      }}
                      onClick={() => setOpenImageDialog(true)}
                    />
                  </Tooltip>
                ) : (
                  <Box sx={{ textAlign: 'center' }}>
                    <ImageIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2">Product image should be less than 100KB</Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Image Dialog */}
          <Dialog
            open={openImageDialog}
            onClose={() => setOpenImageDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogContent>
              <Box 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center' 
                }}
              >
                {image && (
                  <img 
                    src={image.preview} 
                    alt="Full Preview" 
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '50vh', 
                      objectFit: 'contain' 
                    }} 
                  />
                )}
              </Box>
            </DialogContent>
          </Dialog>

          {/* Mapped Data Table Header with Refresh Button */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="medium">
              Mapped Chicken Products
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRefreshMappedItems}
              disabled={fetchingMappedItems}
            >
              {fetchingMappedItems ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Box>

          {/* Mapped Data Table */}
          <TableContainer sx={{ maxHeight: 300 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>S.No</TableCell>
                  <TableCell>Item Code</TableCell>
                  <TableCell>Item Name</TableCell>
                  <TableCell>EAN Number</TableCell>
                  <TableCell>STD</TableCell>
                  <TableCell>Unique No</TableCell>
                  <TableCell>Thumbnail</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fetchingMappedItems ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={30} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : mappedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No product mappings found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  mappedData.map((item) => (
                    <TableRow key={item.sno} hover>
                      <TableCell>{item.sno}</TableCell>
                      <TableCell>{item.itemCode}</TableCell>
                      <TableCell>{item.itemName}</TableCell>
                      <TableCell>{item.eanNumber}</TableCell>
                      <TableCell>{item.std}</TableCell>
                      <TableCell>{item.uniqueNo}</TableCell>
                      <TableCell>
                        {item.thumbnail ? (
                          <Tooltip title="View Image">
                            <img 
                              src={item.thumbnail} 
                              alt="Thumbnail"
                              style={{
                                width: '50px', 
                                height: '50px', 
                                objectFit: 'cover', 
                                borderRadius: '8px', 
                                cursor: 'pointer'
                              }}
                              onClick={() => {
                                setImage({ preview: item.thumbnail });
                                setOpenImageDialog(true);
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">No image</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton 
                          size="small"
                          color="error"
                          onClick={() => handleDeleteMappedItem(item.sno)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={closeSnackbar} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
    </>
  );
}

export default EAN;